import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import API from "../../../util/api";
import { showErrorModal } from "../../../util/general";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ colors }) => colors.buttonBackground};
  border-radius: 50%;
  width: 36px; height: 36px;
  animation: ${spin} 1s linear infinite;
`;

const OverlayText = styled.div`
  margin-top: 1em;
  color: white;
  font-size: 1.2em;
`;

const Container = styled.div.attrs({ id: "bigbox" })`
  background: #f8f8f8;
  padding: 1.5em;
`;

const Heading = styled.h1`
  margin: 0 0 0.5em;
  color: ${({ colors }) => colors.labelText};
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
`;

const FormSection = styled.div`
  flex: 1 1 240px;
  max-width: 280px;
  margin: 10px 0 0 0;
  text-align: center;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;

  .checkbox-group {
    display: flex;
    gap: 10px;
    margin-bottom: 8px;
  }
  .radio-group {
    text-align: left;
    margin: 8px auto;
    line-height: 1.5;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  input { margin-right: 4px; }
`;

const RadioLabel = styled.label`
  display: block;
  input { margin-right: 4px; }
`;

const SubmitWrapper = styled.div` margin-top: 8px; `;

const SubmitButton = styled.button`
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  padding: 6px 12px;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  &:hover { background: ${({ colors }) => colors.buttonHoverBackground}; }
`;

export default function OtherInternetSafetyReports({ colors }) {
  const [isCSM, setIsCSM] = useState(false);
  const [isEF, setIsEF] = useState(false);
  const [reportType, setReportType] = useState("PDF");
  const [loading, setLoading] = useState(false);

  const downloadAndSave = async (apiFn, defaultName, payload) => {
    setLoading(true);
    try {
      const { blob, filename } = await apiFn(payload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      showErrorModal("Unable to download report: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      is_csm: isCSM ? 1 : 0,
      is_ef:  isEF  ? 1 : 0
    };
    if (!isCSM && !isEF) {
      showErrorModal("Please select at least one checkbox.");
      return;
    }

    if (reportType === "PDF") {
      await downloadAndSave(
        API.downloadOtherCSMReport,
        `OtherCSMReport.pdf`,
        payload
      );
    } else {
      await downloadAndSave(
        API.downloadOtherCSMReportCSV,
        `OtherCSMReport.csv`,
        payload
      );
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <Spinner colors={colors}/>
          <OverlayText>Generating report… please wait</OverlayText>
        </LoadingOverlay>
      )}

      <Container colors={colors}>
        <Heading colors={colors}>
          Other Internet Safety Implementation Reports
        </Heading>
        <br/>

        <Form onSubmit={handleSubmit}>
          <FormSection>
            <div className="checkbox-group">
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={isCSM}
                  onChange={e => setIsCSM(e.target.checked)}
                />
                CSM
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={isEF}
                  onChange={e => setIsEF(e.target.checked)}
                />
                EF
              </CheckboxLabel>
            </div>

            <div className="radio-group">
              <RadioLabel>
                <input
                  type="radio"
                  name="reportType"
                  value="PDF"
                  checked={reportType === "PDF"}
                  onChange={e => setReportType(e.target.value)}
                />
                PDF
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  name="reportType"
                  value="CSV"
                  checked={reportType === "CSV"}
                  onChange={e => setReportType(e.target.value)}
                />
                CSV
              </RadioLabel>
            </div>

            <SubmitWrapper>
              <SubmitButton colors={colors} type="submit">
                Run Report
              </SubmitButton>
            </SubmitWrapper>
          </FormSection>
        </Form>
      </Container>
    </>
  );
}
